import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Form, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import {
  Activity,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  Eye,
  FileText,
  MessageSquare,
  Power,
  Rocket,
  TrendingUp,
} from 'lucide-react';
import { api } from '../utils/apiClient';
import { showToast } from '../utils/toast';
import { formatMoney } from '../components/quote/TotalsPanel';
import { ROUTE } from '../utils/navigation';
import { computeSetupSteps } from '../utils/setupReadiness';
import { isQuoteChatUiEnabled } from '../utils/featureFlags';
import SetupChecklist from './settings/SetupChecklist';
import './dashboard.css';

const KPI_COLORS = {
  blue: { bg: '#eff6ff', fg: '#2563eb' },
  green: { bg: '#ecfdf5', fg: '#16a34a' },
  orange: { bg: '#fff7ed', fg: '#ea580c' },
  purple: { bg: '#f5f3ff', fg: '#7c3aed' },
  red: { bg: '#fef2f2', fg: '#dc2626' },
  pink: { bg: '#fdf2f8', fg: '#db2777' },
};

const PIPELINE_THEME = {
  draft: {
    accent: '#f97316',
    badgeBg: '#f97316',
    iconBg: '#fff7ed',
    iconFg: '#ea580c',
    Icon: FileText,
  },
  sent: {
    accent: '#3b82f6',
    badgeBg: '#3b82f6',
    iconBg: '#eff6ff',
    iconFg: '#2563eb',
    Icon: Eye,
  },
  completed: {
    accent: '#22c55e',
    badgeBg: '#22c55e',
    iconBg: '#ecfdf5',
    iconFg: '#16a34a',
    Icon: CheckCircle2,
  },
};

const KpiCard = ({ icon: Icon, color = 'blue', label, value, sub, onView }) => {
  const { t } = useTranslation();
  const c = KPI_COLORS[color] || KPI_COLORS.blue;
  return (
    <div className="q2c-kpi">
      <div className="q2c-kpi-top">
        <span className="q2c-kpi-label">{label}</span>
        <span className="q2c-kpi-icon" style={{ background: c.bg, color: c.fg }}>
          <Icon size={16} />
        </span>
      </div>
      <div className="q2c-kpi-value">{value}</div>
      {sub != null && sub !== '' && <div className="q2c-kpi-sub">{sub}</div>}
      {onView && (
        <button type="button" className="q2c-kpi-view" onClick={onView}>
          {t('common.view')}
        </button>
      )}
    </div>
  );
};

const PipelineStage = ({ stage, currency, showArrow }) => {
  const { t } = useTranslation();
  const theme = PIPELINE_THEME[stage.id] || PIPELINE_THEME.draft;
  const Icon = theme.Icon;
  const pct = Math.max(0, Math.min(100, Number(stage.percent) || 0));

  return (
    <>
      <div className="q2c-pipeline-card">
        <div className="q2c-pipeline-card-top">
          <span
            className="q2c-pipeline-icon"
            style={{ background: theme.iconBg, color: theme.iconFg }}
          >
            <Icon size={18} />
          </span>
          <span className="q2c-pipeline-badge" style={{ background: theme.badgeBg }}>
            {stage.label}
          </span>
        </div>
        <div className="q2c-pipeline-metrics">
          <div className="q2c-pipeline-metric">
            <span className="q2c-pipeline-metric-value">{stage.count ?? 0}</span>
            <span className="q2c-pipeline-metric-label">{t('dashboard.quotes')}</span>
          </div>
          <div className="q2c-pipeline-metric">
            <span className="q2c-pipeline-metric-value">
              {formatMoney(stage.revenue ?? 0, currency).replace(/\.00$/, '')}
            </span>
            <span className="q2c-pipeline-metric-label">{t('dashboard.revenue')}</span>
          </div>
        </div>
        <div className="q2c-pipeline-progress">
          <div className="q2c-pipeline-progress-track">
            <div
              className="q2c-pipeline-progress-fill"
              style={{ width: `${pct}%`, background: theme.accent }}
            />
          </div>
          <span className="q2c-pipeline-progress-pct">{pct}%</span>
        </div>
      </div>
      {showArrow && (
        <div className="q2c-pipeline-arrow" aria-hidden="true">
          <ChevronRight size={22} />
        </div>
      )}
    </>
  );
};

const SalesRepTable = ({ rows, currency }) => {
  const { t } = useTranslation();
  if (!rows || rows.length === 0) {
    return (
      <div className="text-muted text-center py-4">
        {t('dashboard.noRepActivity')}
      </div>
    );
  }

  return (
    <div className="q2c-rep-table-wrap">
      <table className="q2c-rep-table">
        <thead>
          <tr>
            <th>{t('dashboard.repTable.salesRep')}</th>
            <th>{t('dashboard.repTable.totalQuotes')}</th>
            <th>{t('dashboard.repTable.completed')}</th>
            <th>{t('dashboard.repTable.conversionRate')}</th>
            <th>{t('dashboard.repTable.totalSales')}</th>
            <th>{t('dashboard.repTable.avgQuoteValue')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rate = Number(row.conversionRate) || 0;
            const rateClass =
              rate <= 0 ? 'is-low' : rate >= 50 ? 'is-high' : 'is-mid';
            return (
              <tr key={row.salesRepId}>
                <td>
                  <div className="q2c-rep-name">{row.name || t('dashboard.repTable.unassigned')}</div>
                  {row.email ? <div className="q2c-rep-email">{row.email}</div> : null}
                </td>
                <td>{row.totalQuotes ?? 0}</td>
                <td>{row.completed ?? 0}</td>
                <td>
                  <span className={`q2c-rep-rate ${rateClass}`}>{rate.toFixed(1)}%</span>
                </td>
                <td>{formatMoney(row.totalSales ?? 0, currency)}</td>
                <td>{formatMoney(row.avgQuoteValue ?? 0, currency)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Lightweight SVG area chart — no chart library dependency. */
function ActivityChart({ points }) {
  const width = 560;
  const height = 200;
  const pad = { top: 16, right: 12, bottom: 28, left: 28 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const maxY = Math.max(2, ...points.map((p) => p.count));

  const coords = points.map((p, i) => {
    const x = pad.left + (points.length <= 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const y = pad.top + innerH - (p.count / maxY) * innerH;
    return { x, y, ...p };
  });

  const line =
    coords.length === 0
      ? ''
      : coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ');

  const area =
    coords.length === 0
      ? ''
      : `${line} L ${coords[coords.length - 1].x.toFixed(1)} ${(pad.top + innerH).toFixed(1)} L ${coords[0].x.toFixed(1)} ${(pad.top + innerH).toFixed(1)} Z`;

  const ticks = [0, Math.ceil(maxY / 2), maxY];

  return (
    <div className="q2c-activity-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {ticks.map((t) => {
          const y = pad.top + innerH - (t / maxY) * innerH;
          return (
            <g key={t}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
              <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#94a3b8">
                {t}
              </text>
            </g>
          );
        })}
        {area && <path d={area} fill="rgba(37, 99, 235, 0.12)" />}
        {line && <path d={line} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" />}
        {coords.map((c) => (
          <circle key={c.date} cx={c.x} cy={c.y} r="3.5" fill="#2563eb" />
        ))}
      </svg>
    </div>
  );
}

const DashboardPage = ({ onNavigate, storeCurrency = 'USD' }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [widgetStatus, setWidgetStatus] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [acting, setActing] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [rangeMode, setRangeMode] = useState('14');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metrics = await api.dashboard();
      setData(metrics);
    } catch (err) {
      setError(err.message || t('dashboard.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadWidget = useCallback(async () => {
    setWidgetLoading(true);
    try {
      const res = await api.getWidgetStatus();
      setWidgetStatus(res?.status || null);
    } catch {
      setWidgetStatus(null);
    } finally {
      setWidgetLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.getSettings();
      // API returns `{ settings: StoreSettings }` — normalizeSettings unwraps it.
      setSettings(res || null);
    } catch {
      setSettings(null);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadWidget();
    loadSettings();
  }, [loadDashboard, loadWidget, loadSettings]);

  const readiness = useMemo(
    () => computeSetupSteps(settings, widgetStatus),
    [settings, widgetStatus],
  );

  const publish = async () => {
    if (!readiness.requiredComplete) {
      const first = readiness.missingRequired[0];
      showToast(
        t('dashboard.finishSetupFirst', {
          items: readiness.missingRequired.map((m) => t(m.labelKey)).join(', '),
        }),
        'warning',
      );
      if (first) onNavigate?.(first.route);
      return;
    }
    setActing(true);
    try {
      const res = await api.publishWidget();
      setWidgetStatus(res?.status || null);
      showToast(t('dashboard.widgetLive'), 'success');
    } catch (err) {
      showToast(err.message || t('dashboard.publishFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const disable = async () => {
    setActing(true);
    try {
      const res = await api.disableWidget();
      setWidgetStatus(res?.status || null);
      setDisableOpen(false);
      showToast(t('dashboard.widgetDisabled'), 'success');
    } catch (err) {
      showToast(err.message || t('dashboard.disableFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const activityPoints = useMemo(() => {
    const series = data?.activityOverTime || [];
    if (!series.length) return [];

    if (rangeMode === 'custom' && (fromDate || toDate)) {
      return series.filter((p) => {
        if (fromDate && p.date < fromDate) return false;
        if (toDate && p.date > toDate) return false;
        return true;
      });
    }

    const n = rangeMode === '7' ? 7 : 14;
    return series.slice(-n);
  }, [data, rangeMode, fromDate, toDate]);

  const resetRange = () => {
    setRangeMode('14');
    setFromDate('');
    setToDate('');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const currency = data?.currency || storeCurrency || 'USD';
  const recent = data?.recentQuotes || [];
  const live = Boolean(widgetStatus?.live);
  const growth = Number(data?.monthlyGrowth) || 0;

  return (
    <div className="q2c-dash">
      <div className="q2c-dash-header">
        <div>
          <h2>{t('dashboard.title')}</h2>
          <p>{t('dashboard.subtitle')}</p>
        </div>

        <div className="q2c-dash-widget">
          <span className="q2c-dash-widget-label">{t('dashboard.storefrontWidget')}</span>
          {widgetLoading ? (
            <span className="text-muted small">{t('dashboard.checking')}</span>
          ) : live ? (
            <Badge bg="success" pill>
              {t('dashboard.live')}
            </Badge>
          ) : (
            <Badge bg="secondary" pill>
              {t('dashboard.off')}
            </Badge>
          )}
          <OverlayTrigger
            placement="bottom"
            overlay={
              !live && !readiness.requiredComplete ? (
                <Tooltip id="publish-gate-tip">
                  {t('dashboard.finishSetupFirst', {
                    items: readiness.missingRequired.map((m) => t(m.labelKey)).join(', '),
                  })}
                </Tooltip>
              ) : (
                <span />
              )
            }
          >
            <span className="d-inline-flex">
              <Button
                size="sm"
                variant="primary"
                className="d-flex align-items-center gap-1"
                onClick={publish}
                disabled={acting || live || widgetLoading || !readiness.requiredComplete}
              >
                <Rocket size={14} />
                {t('dashboard.enable')}
              </Button>
            </span>
          </OverlayTrigger>
          <Button
            size="sm"
            variant="outline-danger"
            className="d-flex align-items-center gap-1"
            onClick={() => setDisableOpen(true)}
            disabled={acting || !live || widgetLoading}
          >
            <Power size={14} />
            {t('dashboard.disable')}
          </Button>
        </div>
      </div>

      {error && <div className="alert alert-warning">{error}</div>}

      <SetupChecklist settings={settings} widgetStatus={widgetStatus} onNavigate={onNavigate} />

      <div className="q2c-kpi-grid">
        <KpiCard
          icon={FileText}
          color="blue"
          label={t('dashboard.kpi.newQuotes')}
          value={data?.newQuotes?.count ?? 0}
          sub={t('dashboard.kpiSub.totalValue', {
            value: formatMoney(data?.newQuotes?.totalValue ?? 0, currency),
          })}
          onView={() => onNavigate?.(ROUTE.QUOTES)}
        />
        {isQuoteChatUiEnabled() ? (
          <KpiCard
            icon={MessageSquare}
            color="green"
            label={t('dashboard.kpi.newChats')}
            value={data?.newChats?.count ?? 0}
            sub={
              (data?.newChats?.count ?? 0) === 0
                ? t('dashboard.kpiSub.allCaughtUp')
                : t('dashboard.kpiSub.awaitingReply')
            }
            onView={() => onNavigate?.(ROUTE.CHAT)}
          />
        ) : null}
        <KpiCard
          icon={DollarSign}
          color="orange"
          label={t('dashboard.kpi.totalRevenue')}
          value={formatMoney(data?.totalRevenue ?? 0, currency).replace(/\.00$/, '')}
          sub={t('dashboard.kpiSub.completedQuotes', { count: data?.completedCount ?? 0 })}
        />
        <KpiCard
          icon={Activity}
          color="purple"
          label={t('dashboard.kpi.conversionRate')}
          value={`${Number(data?.conversionRate ?? 0).toFixed(1)}%`}
          sub={t('dashboard.kpiSub.quotesRatio', {
            completed: data?.completedCount ?? 0,
            total: data?.totalQuotes ?? 0,
          })}
        />
        <KpiCard
          icon={Clock}
          color="red"
          label={t('dashboard.kpi.pendingQuotes')}
          value={data?.pendingQuotes?.count ?? 0}
          sub={t('dashboard.kpiSub.potential', {
            value: formatMoney(data?.pendingQuotes?.potentialValue ?? 0, currency),
          })}
          onView={() => onNavigate?.(ROUTE.QUOTES)}
        />
        <KpiCard
          icon={TrendingUp}
          color="purple"
          label={t('dashboard.kpi.averageQuote')}
          value={formatMoney(data?.averageQuote ?? 0, currency).replace(/\.00$/, '')}
          sub={t('dashboard.kpiSub.perQuoteValue')}
        />
        <KpiCard
          icon={Activity}
          color="pink"
          label={t('dashboard.kpi.monthlyGrowth')}
          value={`${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}
          sub={t('dashboard.kpiSub.vsLastMonth')}
        />
        <KpiCard
          icon={Calendar}
          color="green"
          label={t('dashboard.kpi.thisMonth')}
          value={data?.thisMonth?.count ?? 0}
          sub={t('dashboard.kpiSub.value', {
            value: formatMoney(data?.thisMonth?.value ?? 0, currency),
          })}
        />
      </div>

      <section className="q2c-pipeline" aria-label={t('dashboard.pipelineAria')}>
        <div className="q2c-pipeline-header">
          <h3>{t('dashboard.pipelineTitle')}</h3>
          <p>{t('dashboard.pipelineSubtitle')}</p>
        </div>
        <div className="q2c-pipeline-flow">
          {(data?.pipeline || []).map((stage, index, list) => (
            <PipelineStage
              key={stage.id}
              stage={stage}
              currency={currency}
              showArrow={index < list.length - 1}
            />
          ))}
          {(!data?.pipeline || data.pipeline.length === 0) && (
            <div className="text-muted py-3">{t('dashboard.noPipeline')}</div>
          )}
        </div>
      </section>

      <div className="q2c-dash-bottom">
        <div className="q2c-dash-panel">
          <div className="q2c-dash-panel-title">{t('dashboard.activityTitle')}</div>
          <div className="q2c-chart-filters">
            <Form.Select
              size="sm"
              value={rangeMode}
              onChange={(e) => setRangeMode(e.target.value)}
              style={{ maxWidth: 140 }}
            >
              <option value="7">{t('dashboard.last7')}</option>
              <option value="14">{t('dashboard.last14')}</option>
              <option value="custom">{t('dashboard.customRange')}</option>
            </Form.Select>
            <Form.Control
              size="sm"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setRangeMode('custom');
              }}
            />
            <Form.Control
              size="sm"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setRangeMode('custom');
              }}
            />
            <Button size="sm" variant="outline-secondary" onClick={resetRange}>
              {t('common.reset')}
            </Button>
          </div>
          {activityPoints.length === 0 ? (
            <div className="text-muted text-center py-5">{t('dashboard.noActivity')}</div>
          ) : (
            <ActivityChart points={activityPoints} />
          )}
        </div>

        <div className="q2c-dash-panel">
          <div className="q2c-dash-panel-title">{t('dashboard.recentQuotes')}</div>
          {recent.length === 0 ? (
            <div className="text-muted text-center py-4">
              {t('dashboard.noRecentQuotes')}
            </div>
          ) : (
            <ul className="q2c-recent-list">
              {recent.map((q) => (
                <li
                  key={q.id}
                  className="q2c-recent-item"
                  onClick={() => onNavigate?.(`${ROUTE.QUOTES}/${q.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onNavigate?.(`${ROUTE.QUOTES}/${q.id}`);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div>
                    <div className="q2c-recent-title">
                      {t('dashboard.recentQuoteLine', {
                        number: q.number,
                        customer: q.customerName || t('dashboard.customer'),
                      })}
                    </div>
                    <div className="q2c-recent-meta">
                      {q.date
                        ? new Date(q.date).toLocaleDateString(i18n.language, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </div>
                  </div>
                  <div className="q2c-recent-total">{formatMoney(q.total, currency)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <section className="q2c-dash-panel q2c-rep-panel" aria-label={t('dashboard.repPanelAria')}>
        <div className="q2c-dash-panel-title">{t('dashboard.salesRepPerformance')}</div>
        <SalesRepTable rows={data?.salesRepPerformance || []} currency={currency} />
      </section>

      <Modal show={disableOpen} onHide={() => !acting && setDisableOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('dashboard.disableModal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('dashboard.disableModal.body')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDisableOpen(false)} disabled={acting}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={disable} disabled={acting}>
            {acting ? t('dashboard.disabling') : t('dashboard.disable')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardPage;
